import { SimpleDeferred } from '@tableau/vizportalclient-common-components';
import * as moment from 'moment-timezone';
import * as _ from 'underscore';

import {refreshSessionInfo} from '../app/actions/RefreshSessionInfoAction';
import {incrementRouterSpaceReactKey} from '../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import {getAppStore} from '../app/AppStore';
import {
  CompositeSessionInfoSelectors, selectSessionUser, SessionInfoSelectors,
  SessionServerSelectors,
  SessionSiteSelectors,
  SessionUserSelectors,
} from '../app/selectors/SessionInfoSelectors';
import {AppState} from '../app/state/AppState';
import {Messages} from '../codegen/Localize';
import {LocalizationLoader} from '../commonInfra/services/LocalizationLoader';
import {SessionService} from '../commonInfra/services/SessionService';
import {WindowLocationService} from '../commonInfra/services/WindowLocationService';
import AxiosHttpClient from '../httpClient/AxiosHttpClient';
import {Request} from '../httpClient/serverApi/requests/Request';
import {IError} from '../httpClient/serverApi/types/IError';
import {IResult} from '../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../httpClient/WebHttpClient';
import {clearMetricsData} from '../metrics/actions/MetricsDataActions';
import {MobileAppService} from '../mobile/services/MobileAppService';
import {ServerSettingsUnauthenticated} from '../settings/services/ServerSettingsUnauthenticated';
import { ICustomLogo} from '../settings/types/IServerSettingsUnauthenticated';
import { ExtractEncryptionMode } from '../sites/SitePropertyTypes';
import {FeatureFlags} from '../ts/FeatureFlags';
import {FeatureFlagsHelper} from '../ts/FeatureFlagsHelper';
import {CommonErrorCode, SiteSwitchErrorCodes} from '../ts/helpers/ErrorCodes';
import {L10n} from '../ts/L10n/L10n';
import {Set} from '../ts/models/Set';
import {CreateApiTokenRequest} from '../ts/ServerApi/requests/CreateApiTokenRequest';
import {DeleteApiTokenRequest} from '../ts/ServerApi/requests/DeleteApiTokenRequest';
import {GeneratePublicKeyRequest, IGeneratePublicKeyResult} from '../ts/ServerApi/requests/GeneratePublicKeyRequest';
import {GetApiTokenRequest} from '../ts/ServerApi/requests/GetApiTokenRequest';
import {GetFeatureValueRequest} from '../ts/ServerApi/requests/GetFeatureValueRequest';
import {GetSessionInfoRequest, IGetSessionInfoResult} from '../ts/ServerApi/requests/GetSessionInfoRequest';
import {InitializeServerWithActiveDirectoryUserRequest} from '../ts/ServerApi/requests/InitializeServerWithActiveDirectoryUserRequest';
import {InitializeServerWithExternalUserRequest} from '../ts/ServerApi/requests/InitializeServerWithExternalUserRequest';
import {InitializeServerWithLocalUserRequest} from '../ts/ServerApi/requests/InitializeServerWithLocalUserRequest';
import {RecordNavigationTimingRequest} from '../ts/ServerApi/requests/RecordNavigationTimingRequest';
import {SwitchSiteRequest} from '../ts/ServerApi/requests/SwitchSiteRequest';
import {
  IUpdateUserLocalTimeZoneDataParams,
  UpdateUserLocalTimeZoneDataRequest,
} from '../ts/ServerApi/requests/UpdateUserLocalTimeZoneDataRequest';
import {IFeatureFlagsDoNotUse} from '../ts/ServerApi/types/IFeatureFlagsDoNotUse';
import {ILoginParams} from '../ts/ServerApi/types/ILoginParams';
import {IOAuthCredentialTypeInfo} from '../ts/ServerApi/types/IOAuthCredential';
import {IServerVersionInfo, ISessionInfo, IUserSessionInfo, IUserTimeZone} from '../ts/ServerApi/types/ISessionInfo';
import {SiteRoles} from '../ts/ServerApi/types/ISiteRole';
import {IToast, ToasterService, ToastType} from '../ts/services/ToasterService';

// Start at 7-digit numbers so these don't collide with server error codes
export enum ErrorReason {
  Canceled = 1000001,
  ResourceNotFound = 1000002,
  Unauthorized = 1000003,
  Unknown = 1000004,
  EmptyResponse = 1000005,
}

export enum AuthLevel {
  Anonymous,
  Authenticated,
  SiteAdmin,
  SelfOrAdmin,
  ServerAdmin
}

class SessionManagementService {
  private caseInsensitiveSiteUrlNames: Set<string>;
  protected sessionInfoPromise: Promise<void>;
  protected numSessionInfoRequests: number;
  private rebuildingSearchIndexToast: IToast;
  private publishingSamplesToast: IToast;
  private toaster = ToasterService.instance;
  private deferUntilSessionExists: SimpleDeferred<void>;

  constructor() {
    this.numSessionInfoRequests = 0;
    this.sessionInfoPromise = null;
    this.caseInsensitiveSiteUrlNames = new Set((siteUrlName: string) => siteUrlName.toLowerCase());
  }

  /**
   * Make a server request that needs to be made within a valid session.
   * If there is a request pending that might change the session, wait until that request is finished before sending this one.
   */
  public sendRequestWithinSession<TResult>(makeRequest: () => Promise<TResult>): Promise<TResult> {
    // Wait until any pending session info request is resolved before attempting the request
    if (this.numSessionInfoRequests > 0) {
      return Promise.resolve(this.sessionInfoPromise.then(makeRequest));
    }
    return makeRequest();
  }

  /**
   * Requests that could change the session must make the request through this method
   */
  public sessionInfoRequest(makeRequest: () => Promise<any>): Promise<any> {
    if (this.numSessionInfoRequests > 0) {
      // If existing session info request, make this request once it is completed.
      this.sessionInfoPromise = this.sessionInfoPromise.then(makeRequest);
    } else {
      // TODO: remove the static cast
      this.sessionInfoPromise = makeRequest() as Promise<any>;
    }
    this.numSessionInfoRequests++;
    this.sessionInfoPromise.finally(() => {
      this.numSessionInfoRequests--;
      if (this.deferUntilSessionExists && this.numSessionInfoRequests === 0 && this.inSession()) {
        this.deferUntilSessionExists.resolve();
      }
    }).catch(err => err);
    return this.sessionInfoPromise;
  }

  public initialize(username: string, password: string, displayName: string): Promise<void> {
    return this.sessionInfoRequest(() => {
      return WebHttpClient.requestPost(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          const encryptedPassword = rsa.encrypt(password, generatePublicKeyResult.key);
          let initializeRequest: Request<ILoginParams, ISessionInfo>;

          if (ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
            initializeRequest = new InitializeServerWithActiveDirectoryUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
            });
          } else if (ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
            initializeRequest = new InitializeServerWithLocalUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
              displayName: displayName,
            });
          } else if (ServerSettingsUnauthenticated.isServerDomainFamilyExternal()) {
            initializeRequest = new InitializeServerWithExternalUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
            });
          } else {
            throw new Error('Unknown domain family type. Current implementation only supports: AD, local, and external.');
          }
          return WebHttpClient.requestPost(initializeRequest)
            .then((initializeResult: ISessionInfo) => {
              if (initializeResult.errors) {
                return Promise.reject(initializeResult.errors[0] && initializeResult.errors[0].code);
              }
              return this.applySessionInfoInClient(initializeResult);
            });
        });
    });
  }


  /**
   * Returned promise will be fulfilled. All rejections are handled.
   * If there is an error, the code will be returned in the fulfilled handler.
   */
  public refreshSessionInfo(checkForEqualSite: boolean = false): Promise<any> {
    const previousUserId = this.sessionUserId();
    return this.sessionInfoRequest(() => {
      return WebHttpClient.requestPost(new GetSessionInfoRequest())
        .then((result: IGetSessionInfoResult) => {
          if (!result) {
            this.clearSessionInfo('error getting session info');
            return null;
          }
          if (result.errors) {
            this.clearSessionInfo(result.errors[0] && result.errors[0].code + '');
            return result.errors[0] && result.errors[0].code;
          } else {
            // in certain cases we only want to update session info if the returned session info matches the current site.
            // TFSID:953116. Switching quickly between two tabs of different sites on same server leads to bugs.
            // This site equality check prevents that
            if (checkForEqualSite && SessionSiteSelectors.siteId(this.appState()) !== result.site.id) {
              return;
            }
            return this.applySessionInfoInClient(result)
              .then((result) => {
                this.reloadStateIfUserChanged(previousUserId);
                return result;
              });
          }
        }, (errorResult: any) => {
          // TFSID 1067441: See AuthenticationService.request for more information. Don't clear the session when there is an
          // empty response. Empty responses mean that the request was aborted, and clearing the session in this case
          // breaks deep linking + makes for a bad UX.
          if (errorResult !== ErrorReason.EmptyResponse) {
            this.clearSessionInfo('error getting session info: ' + errorResult);
          }
        })
        .catch((reason: any) => {
          // See above comment!
          if (reason !== ErrorReason.EmptyResponse) {
            this.clearSessionInfo(reason);
          }
          return reason;
        });
    });
  }

  public hasCreatorTier(): boolean {
    return this.siteCapacityCheckEnabled() ? this.siteHasCreatorCapacity() : this.serverHasCreatorCapacity();
  }

  public hasViewerTier(): boolean {
    if (this.hasCreatorTier()) {
      return true;
    }
    return this.siteCapacityCheckEnabled() ? this.siteHasViewerCapacity() : this.serverHasViewerCapacity();
  }

  /**
   * Loads all state needed to render the app
   * Returns a promise that fulfills with a code indicating bootstrap state
   */
  public bootstrapSession(): Promise<number> {
    let sessionInfoNoSessionReason: number = null;
    return Promise.all([
      ServerSettingsUnauthenticated.fetchServerSettingsUnauthenticated(),
      this.refreshSessionInfo(),
    ]).then((results: any[]):  Promise<any> => {
      // Try to deep link to the mobile app as soon as possible, to avoid the mobile browser's auth dialog popping up,
      // which prevents iOS Safari from showing the deep linking dialog "Open this page in Tableau".
      // Note: If we try to do this in run.ts as the first thing when we initialize, the redirects triggered by
      // the deep linking logic interfere with the completion of getSessionInfo calls resulting in the user getting
      // signed out.
      // For more details and vizclient counterpart, see https://reviewboard.prod.tableautools.com/r/253096
      // Also, we need to get server settings first so as not to deep link if the setting has been turned off on server.
      if (ServerSettingsUnauthenticated.isEnableDeepLinking()) {
        MobileAppService.tryDeepLinkOnLoad(WindowLocationService);
      }

      sessionInfoNoSessionReason = results[1];
      // Now that we have server settings and session info we can determine the language to display

      const language = this.sessionLanguage() ?? ServerSettingsUnauthenticated.serverLanguage();
      const locale = this.sessionLocale() ?? ServerSettingsUnauthenticated.serverLocale();
      return LocalizationLoader.updateLanguageAndLocaleInClient(language, locale);
    }).then((): Promise<number> => {
      return  Promise.resolve(sessionInfoNoSessionReason);
    });
  }

  /**
   * This won't handle errors. Use SiteSwitchService.switchIfNeeded
   * to get the proper error feedback
   */
  public switchSiteIfNeeded_DoNotCallDirectly(urlName: string, podRedirectPath?: string): Promise<any> {
    return this.sessionInfoRequest(() => {
      // falsy values should be empty string for default site
      urlName = urlName || '';

      if (!this.isCurrentSiteUrlName(urlName)) {

        return WebHttpClient.requestPost(new SwitchSiteRequest({urlName: urlName}))
          .then((sessionInfo: ISessionInfo) => {
            if (sessionInfo && sessionInfo.errors) {
              const error = sessionInfo.errors[0];
              if (error && error.code === SiteSwitchErrorCodes.AttemptedToSwitchSiteToCurrentSite) {
                // No-op, do nothing
                // Remember that this particular site url name is case insensitive
                this.caseInsensitiveSiteUrlNames.add(urlName);
                this.refreshSessionInfo(); // TFSID: 503672 refresh in case stale data caused this
                return;
              }
              return Promise.reject(error);
            }
            this.applySessionInfoInClient(sessionInfo);
            getAppStore().dispatch(clearMetricsData());
          }, (error: IError) => {
            if (error && error.code === CommonErrorCode.RedirectToOtherPod) {
              this.redirectToOtherPod(error, podRedirectPath);
              // If redirected, defer indefinitely, to prevent further execution here
              return new SimpleDeferred().promise;
            }
          });
      }
      return Promise.resolve(null);
    });
  }

  public recordNavigationTiming(timing: PerformanceTiming): void {
    //==== IE8 bug workaround: ====
    // Convert to plain ol' object with only the numbers from the browser's PerformanceTiming type.
    // Needed in order to send in request body in IE8.
    let timingObj = _.clone(timing);
    // For non IE8 browsers, remove functions (e.g. toJSON) so the browser doesn't attempt to serialize them.
    timingObj = _.omit(timingObj, _.functions(timingObj));

    WebHttpClient.requestPost(new RecordNavigationTimingRequest({timing: timingObj}));
  }

  public redirectToOtherPod(error: IError, destinationPodPath: string): void {
    const destinationPodUrl: string = AxiosHttpClient.getDestinationPodFromError(error);
    const dest = destinationPodUrl + destinationPodPath;
    WindowLocationService.navigateToWithoutAddingToHistory(dest);
  }

  public isCurrentSiteUrlName(urlName: string): boolean {
    if (this.caseInsensitiveSiteUrlNames.contains(urlName)) {
      return this.sessionSiteUrlName().toLowerCase() === urlName.toLowerCase();
    }

    return this.sessionSiteUrlName() === urlName;
  }

  // username and domainName are only needed for checking SELF
  public isUserAuthorized(role: AuthLevel, userName: string = '', domainName: string = ''): boolean {
    switch (role) {
      case AuthLevel.Anonymous:
      default:
        return true;
      case AuthLevel.Authenticated:
        return this.inSession();
      case AuthLevel.SiteAdmin:
        return this.isSessionSiteOrServerAdmin();
      case AuthLevel.SelfOrAdmin:
        // for the user place page, return true if an admin or the URL contains the
        // username and domainName of the session user
        return this.isSessionSiteOrServerAdmin() || this.isSessionUserSelf(userName, domainName);
      case AuthLevel.ServerAdmin:
        return this.isSessionServerAdmin();
    }
  }

  /**
   * This is a convenience method to get the app state for the various session info selectors below
   * as we port their sources of truth to `SessionInfoSelectors.tsx` over time.
   */
  private appState(): AppState {
    return getAppStore().getState();
  }

  public sessionUserInfo(): IUserSessionInfo {
    return selectSessionUser(this.appState());
  }

  public isViewerUser(): boolean {
    return SessionSiteSelectors.siteRole(this.appState()) === SiteRoles.Viewer;
  }

  public sessionUserDisplayName(): string {
    return SessionUserSelectors.sessionUserDisplayName(this.appState()) || '';
  }

  public sessionUserId(): string {
    return SessionUserSelectors.sessionUserId(this.appState());
  }

  public sessionAuthUserId(): string {
    return SessionUserSelectors.authId(this.appState());
  }

  public sessionSiteLuid(): string {
    return SessionSiteSelectors.luid(this.appState());
  }

  public sessionUsername(): string {
    return SessionUserSelectors.sessionUserName(this.appState());
  }

  public sessionUserDomainName(): string {
    return SessionUserSelectors.sessionDomainName(this.appState());
  }

  public isDataAlertsEnabled(): boolean {
    return SessionSiteSelectors.isDataAlertsEnabled(this.appState());
  }

  public catalogingOnSiteEnabled(): boolean {
    return FeatureFlags.metadataAreaEnabled &&
      SessionSiteSelectors.catalogingOnSiteEnabled(this.appState()) !== false;
  }

  public catalogingOnServerEnabled(): boolean {
    return FeatureFlags.metadataAreaEnabled && SessionServerSelectors.catalogingOnServerEnabled(this.appState()) !== false;
  }

  public flowsOnSiteEnabled(): boolean {
    return SessionSiteSelectors.flowsOnSiteEnabled(this.appState()) !== false;
  }

  public flowsOnServerEnabled(): boolean {
    return SessionServerSelectors.flowsOnServerEnabled(this.appState()) !== false;
  }

  public flowProcessorEnabled(): boolean {
    return this.flowsOnServerEnabled() || FeatureFlags.MaestroFlowEditingServerV1;
  }

  public editingFlowsEnabled(): boolean {
    return SessionSiteSelectors.editingFlowsEnabled(this.appState()) !== false;
  }

  public schedulingFlowsEnabled(): boolean {
    return SessionSiteSelectors.schedulingFlowsEnabled(this.appState()) !== false;
  }

  public get isAfeUiSettingEnabled(): boolean {
    // Linter here is a false alarm – this function's name starts with "use" but is not a React hook
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return SessionServerSelectors.useAlternativeFederationEngine(this.appState()) === true;
  }

  public canSessionUserEditEmbeddedCredentials(): boolean {
    return this.isSessionSiteOrServerAdmin() || SessionServerSelectors.embeddedCredentialsEnabled(this.appState());
  }

  public isSessionUserSelf(userName: string, domainName: string): boolean {
    return SessionUserSelectors.sessionUserName(this.appState()) === userName
      && SessionUserSelectors.sessionDomainName(this.appState()) === domainName;
  }

  public isSessionServerAdmin(): boolean {
    return SessionUserSelectors.isSessionServerAdmin(this.appState());
  }

  public isSessionSiteAdmin(): boolean {
    return SessionSiteSelectors.isSessionSiteAdmin(this.appState());
  }

  public isSessionSiteOrServerAdmin(): boolean {
    return CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(this.appState());
  }

  public sessionSiteId(): string {
    return SessionSiteSelectors.siteId(this.appState());
  }

  public sessionSiteRole(): string {
    return SessionSiteSelectors.siteRole(this.appState());
  }

  public sessionServerId(): string {
    return SessionServerSelectors.logicalServerInstanceId(this.appState()) || '';
  }

  public sessionSiteUrlName(): string {
    return SessionSiteSelectors.siteUrlName(this.appState());
  }

  public sessionSiteName(): string {
    return SessionSiteSelectors.siteName(this.appState());
  }

  public sessionServerVersion(): IServerVersionInfo {
    return SessionServerSelectors.version(this.appState()) || null;
  }

  public sessionServerPodId(): string {
    return SessionServerSelectors.podId(this.appState()) || null;
  }

  public sessionSupportHost(): string {
    return SessionServerSelectors.supportHost(this.appState());
  }

  public sessionSupportPath(): string {
    return SessionServerSelectors.supportPath(this.appState());
  }

  public webstoreUrl(): string {
    return SessionServerSelectors.webstoreUrl(this.appState()) || '';
  }

  public isSessionMultiSite(): boolean {
    return SessionUserSelectors.numberOfSites(this.appState()) > 1;
  }

  public isSingleSiteOnPremServer(): boolean {
    return !this.isSessionMultiSite() && !this.isOnlineEnvironment();
  }

  public isBuyLicensesEnabled(): boolean {
    return SessionSiteSelectors.buyLicensesEnabled(this.appState());
  }

  public siteLogo(): ICustomLogo {
    return SessionSiteSelectors.customHeaderLogo(this.appState());
  }

  public compactLogo(): ICustomLogo {
    return SessionSiteSelectors.customCompactLogo(this.appState());
  }

  public isSiteCustomizationEnabled(): boolean {
    return SessionServerSelectors.siteCustomizationEnabled(this.appState());
  }

  public isRecommendationsEnabled(): boolean {
    return SessionServerSelectors.recommendationsEnabled(this.appState());
  }

  public isVizRecommendationsEnabled(): boolean {
    return SessionServerSelectors.vizRecommendationsEnabled(this.appState());
  }

  public isSiteAdminSettingsEnabled(): boolean {
    return SessionServerSelectors.siteAdminSettingsEnabled(this.appState());
  }

  public siteUuid(): string {
    return SessionSiteSelectors.siteUuid(this.appState()) || null;
  }

  public siteTypeName(): string {
    return SessionSiteSelectors.siteTypeName(this.appState()) || null;
  }

  /**
   * Returns a promise that resolves when all pending session info requests are completed.
   * This can be used in places where we don't want to get properties from the session when we know
   * they might be changing.
   * NOTE: This promise does not guarantee that there is a session when resolved; it only guarantees
   * that there are no in-progress pending API requests to fetch the session info. If you need to wait
   * for an actual session (e.g. at the login screen), use waitForSession() instead.
   */
  public getSessionInfoPromise(): Promise<void> {
    return this.sessionInfoPromise ? this.sessionInfoPromise : Promise.resolve();
  }

  /**
   * Returns a promise that resolves when all pending session info requests are completed, AND
   * when there is a valid session. i.e. the promise will not resolve until after the user logs in.
   * This is different than getSessionInfoPromise(), which only looks at pending session info
   * requests and may resolve without a valid session.
   */
  public waitForSession(): Promise<void> {
    if (this.inSession()) {
      return this.getSessionInfoPromise();
    }

    if (!this.deferUntilSessionExists) {
      this.deferUntilSessionExists = new SimpleDeferred<void>();
    }
    return this.deferUntilSessionExists.promise;
  }

  public updateDisplayNameAllowed(): boolean {
    return SessionServerSelectors.updateDisplayNameAllowed(this.appState());
  }

  public defaultSubscriptionsEmail(): string {
    return SessionServerSelectors.defaultSubscriptionsEmail(this.appState());
  }

  public featureFlagsDoNotUse(): IFeatureFlagsDoNotUse {
    return SessionServerSelectors.featureFlagsDoNotUse(this.appState());
  }

  public changePasswordUrl(): string {
    return SessionServerSelectors.changePasswordUrl(this.appState());
  }

  public manageMfaUrl(): string {
    return this.isMfaEnabled() && SessionServerSelectors.manageMfaUrl(this.appState());
  }

  public datasourceHelpUrl(): string {
    return SessionServerSelectors.datasourceHelpUrl(this.appState());
  }

  public offlineHelpEnabled(): boolean {
    return SessionServerSelectors.offlineHelpEnabled(this.appState());
  }

  public helpEdition(): string {
    return SessionServerSelectors.helpEdition(this.appState());
  }

  public canSessionUserAuthorOnWeb(): boolean {
    return SessionUserSelectors.canAuthorOnWeb(this.appState());
  }

  public canSessionUserManageUsers(): boolean {
    return SessionUserSelectors.canManageUsers(this.appState());
  }

  public getStartPage(): string {
    return SessionUserSelectors.startPage(this.appState()) || '/';
  }

  public userTimeZone(): IUserTimeZone {
    return SessionUserSelectors.userTimeZone(this.appState());
  }

  public defaultAskDataMode(): string {
    return SessionServerSelectors.defaultNewSiteAskDataMode(this.appState()) || '';
  }

  public isAdminViewsDisabled(): boolean {
    return SessionServerSelectors.adminViewsDisabled(this.appState());
  }

  public isAdminDataSourceEnabled(): boolean {
    return SessionSiteSelectors.adminInsightsEnabled(this.appState());
  }

  private siteHasCreatorCapacity(): boolean {
    return SessionSiteSelectors.serverSiteCapacityInfo(this.appState())?.hasCreatorCapacity;
  }

  private siteHasViewerCapacity(): boolean {
    return SessionSiteSelectors.serverSiteCapacityInfo(this.appState())?.hasViewerCapacity;
  }

  // Server Settings

  private serverHasCreatorCapacity(): boolean {
    return SessionServerSelectors.serverSiteCapacityInfo(this.appState())?.hasCreatorCapacity;
  }

  private serverHasViewerCapacity(): boolean {
    return SessionServerSelectors.serverSiteCapacityInfo(this.appState())?.hasViewerCapacity;
  }

  public siteCapacityCheckEnabled(): boolean {
    return SessionServerSelectors.siteCapacityCheckEnabled(this.appState());
  }

  public shouldShowServerLicensingTierInfo(): boolean {
    return this.isSessionServerAdmin();
  }

  public hasSubscriptionTieredLicensing(): boolean {
    return this.hasCreatorTier() || this.hasViewerTier();
  }

  public displaySchedulesInClientTimezone(): boolean {
    return SessionServerSelectors.displaySchedulesInClientTimezone(this.appState());
  }

  public displayScheduleDescriptionAsName(): boolean {
    return SessionServerSelectors.displayScheduleDescriptionAsName(this.appState());
  }

  public isRunNowEnabled(): boolean {
    return SessionServerSelectors.runNowOnServerEnabled(this.appState());
  }

  public isOnlineEnvironment(): boolean {
    return this.getHelpEdition() === 'online';
  }

  public isNotificationEnabledForServer(): boolean {
    return SessionServerSelectors.notificationEnabledForServer(this.appState());
  }

  public isNotificationEnabledForSite(): boolean {
    return SessionSiteSelectors.notificationEnabled(this.appState());
  }

  public isSiteInviteEmailEnabledForServer(): boolean {
    return SessionServerSelectors.siteInviteBasicEmailEnabled(this.appState());
  }

  public isWelcomeChannelEnabledForUser(): boolean {
    return SessionUserSelectors.showWelcomeScreen(this.appState());
  }

  public isMetricsBannerEnabledForUser(): boolean {
    return SessionUserSelectors.showMetricsBanner(this.appState());
  }

  public isAddSupportUserEnabledForServer(): boolean {
    return SessionServerSelectors.featureFlagsDoNotUse(this.appState()).addSupportUserEnabled;
  }

  public getBuyLicensesDisplayType(): string {
    return SessionSiteSelectors.buyLicenseButtonDisplayType(this.appState());
  }

  public isMfaEnabled(): boolean {
    return SessionSiteSelectors.mfaEnabled(this.appState());
  }

  public getFeatureValue(serviceName: string, defaultValue: boolean): Promise<any> {
    return WebHttpClient.sendRequest(new GetFeatureValueRequest(serviceName, String(defaultValue))).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public getApiTokens(): Promise<any> {
    return WebHttpClient.sendRequest(new GetApiTokenRequest()).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public createApiToken(clientName: string): Promise<any> {
    return WebHttpClient.sendRequest(new CreateApiTokenRequest(clientName)).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public deleteApiToken(tokenId: string): Promise<any> {
    return WebHttpClient.sendRequest(new DeleteApiTokenRequest(tokenId)).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public hideAdminVizPerformanceofFlowRuns(): boolean {
    return !(this.hideAdminVizPerformanceofFlowRunsOnSite() && this.flowProcessorEnabled());
  }

  public hideAdminVizPerformanceofFlowRunsOnSite(): boolean {
    return !(this.flowProcessorEnabled() && this.flowsOnSiteEnabled());
  }

  public isSelfServiceScheduleEnabled(): boolean {
    return SessionServerSelectors.featureFlagsDoNotUse(this.appState()).selfServiceSchedulesEnabled;
  }

  public isLimitedUserVisibilityEnforced(): boolean {
    return CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(this.appState());
  }

  public getExtractEncryptionModeForSite(): ExtractEncryptionMode {
    return SessionSiteSelectors.extractEncryptionMode(this.appState());
  }

  public isExtractEncryptionEnabledOmniSwitch(): boolean {
    // Backend checks for 1) Feature Flags and 2) environment specific checkes. (e.g. online vs onprem) for populating sessionInfo.server.extractEncryptionEnabled
    return SessionServerSelectors.extractEncryptionEnabled(this.appState()) === true;
  }

  public isNamedSharingEnabled() {
    // This will check and return site setting as well as this feature is controlled by FF and site setting and if current user is guest.
    return SessionSiteSelectors.isNamedSharingEnabled(this.appState());
  }

  public isRequestAccessEnabled() {
    return SessionSiteSelectors.isRequestAccessEnabled(this.appState());
  }

  public analyticsEndpoint(): string {
    return SessionServerSelectors.analyticsEndpoint(this.appState());
  }

  private reloadStateIfUserChanged(previousUserId: string) {
    const newUserId = this.sessionUserId();
    if (previousUserId && newUserId && newUserId !== previousUserId) {
      getAppStore().dispatch(incrementRouterSpaceReactKey());
    }
  }

  public ignoreExtractTaskPriority(): boolean {
    return SessionServerSelectors.ignoreExtractTaskPriority(this.appState());
  }

  public maxDescriptionLength(): number {
    return SessionServerSelectors.maxDescriptionLength(this.appState());
  }

  private getHelpEdition(): string {
    return SessionServerSelectors.helpEdition(this.appState());
  }

  public getSessionIdleLimitSeconds() {
    // TODO: remove the 5 minute default when the backend implements this. B144748
    return SessionServerSelectors.sessionIdleLimitSeconds(this.appState()) || 5 * 60;
  }

  public uploadSAMLMetadata(file: File): Promise<IResult> {
    const formData = new FormData();
    formData.append('entity_descriptor', file);
    formData.append(SessionService.getSessionCookieName(), SessionService.getSessionId());
    return WebHttpClient.requestPostFormToRestApi(formData, 'updateIdentityProvider').then((result: IResult) => {
      if (result.errors) {
        return Promise.reject(result.errors[0] && result.errors[0].code);
      } else {
        return result;
      }
    });
  }

  public applySessionInfoInClient(newSessionInfo: ISessionInfo): Promise<any> {
    SessionService.createSession();
    getAppStore().dispatch(refreshSessionInfo(newSessionInfo));
    if (newSessionInfo && newSessionInfo.server) {
      FeatureFlagsHelper.overwriteFeatureFlagsFromServerFeatureFlags(newSessionInfo.server.featureFlags, FeatureFlags);
    }
    this.updateUserTimeZone();

    const newLanguage = this.sessionLanguage() ?? ServerSettingsUnauthenticated.serverLanguage();
    const newLocale = this.sessionLocale() ?? ServerSettingsUnauthenticated.serverLocale();

    return LocalizationLoader.updateLanguageAndLocaleInClient(newLanguage, newLocale).then(() => {
      this.showOrHideRebuildingSearchIndexToast();
      this.showOrHidePublishingSamplesToast();
    });
  }

  public isSandboxEnabledByDefaultForNewSites(): boolean {
    return FeatureFlags.personalSpaceEnabled && SessionServerSelectors.personalSandboxByDefaultEnabled(this.appState());
  }

  public oauthSettings(): Array<IOAuthCredentialTypeInfo> {
    return SessionServerSelectors.oauthSettings(this.appState());
  }

  public isSessionRestrictedTrustedTicket(): boolean {
    return SessionInfoSelectors.isSessionRestrictedTrustedTicket(this.appState());
  }

  public sessionLanguage(): string {
    return SessionUserSelectors.language(this.appState());
  }

  public sessionLocale(): string {
    return SessionUserSelectors.locale(this.appState());
  }

  public rebuildingSearchIndex(): boolean {
    return SessionServerSelectors.rebuildingSearchIndex(this.appState());
  }

  public publishingSamples(): boolean {
    return SessionServerSelectors.publishingSamples(this.appState());
  }

  public isUserSetTimeZone(): boolean {
    return SessionUserSelectors.isUserSetTimeZone(this.appState());
  }

  public defaultDomain(): string {
    return SessionServerSelectors.defaultDomain(this.appState());
  }

  /**
   * Uses moment.js to guess client time zone and makes a request to update.
   */
  public updateUserTimeZone() {

    if (this.isUserSetTimeZone() != null) {
      // only guess the timezone once and do not override user set time zone
      return;
    }
    const date = new Date();

    const timeZoneId = moment.tz.guess();
    const updateUserLocalTimeZoneDataParams: IUpdateUserLocalTimeZoneDataParams = {
      offset: date.getTimezoneOffset(),
      timeZoneId: timeZoneId,
      isUserSetTimeZone: false, // this is a system guess not a user action
    };
    const updateUserLocalTimeZoneDataRequest =
      new UpdateUserLocalTimeZoneDataRequest(updateUserLocalTimeZoneDataParams);
    WebHttpClient.requestPost(updateUserLocalTimeZoneDataRequest);
  }

  public inSession(): boolean {
    return !!SessionService.isSessionCurrentlyValid();
  }

  public isPersonalSpaceOnSiteEnabled(): boolean {
    return SessionSiteSelectors.isPersonalSpaceOnSiteEnabled(this.appState());
  }

  public shouldShowPersonalSpace(): boolean {
    return SessionSiteSelectors.shouldShowPersonalSpace(this.appState());
  }

  public isDashboardExtensionsEnabled(): boolean {
    return SessionServerSelectors.dashboardExtensionsEnabled(this.appState());
  }

  private showOrHideRebuildingSearchIndexToast() {
    if (this.inSession() && L10n.language && this.rebuildingSearchIndex()) {
      if (!this.rebuildingSearchIndexToast || this.rebuildingSearchIndexToast.isClosed) {
        this.toaster.pop({
          type: ToastType.Error,
          text: Messages.rebuildingSearchIndexMessage(),
        });
        this.rebuildingSearchIndexToast = this.toaster.toasts[this.toaster.toasts.length - 1];
      }
    } else {
      if (this.rebuildingSearchIndexToast) {
        this.toaster.close(this.rebuildingSearchIndexToast.id);
      }
    }
  }

  private showOrHidePublishingSamplesToast() {
    if (this.inSession() && L10n.language && this.publishingSamples()) {
      if (!this.publishingSamplesToast || this.publishingSamplesToast.isClosed) {
        this.toaster.pop({
          type: ToastType.Info,
          text: Messages.publishingSamplesMessage(),
          timeoutDelay: 60 * 1000, // 60 seconds
        });
        this.publishingSamplesToast = this.toaster.toasts[this.toaster.toasts.length - 1];
      }
    } else {
      if (this.publishingSamplesToast) {
        this.toaster.close(this.publishingSamplesToast.id);
      }
    }
  }

  public clearSessionInfo(message?: string) {

    // If someone is waiting for a session, but we didn't have a session, let them keep waiting.
    // Clear out the defer only if we were already in a session, at which point any clients who were
    // waiting for a session would have already had their promise resolved.
    if (this.inSession() && this.deferUntilSessionExists) {
      this.deferUntilSessionExists.reject('Session is being cleared');
      this.deferUntilSessionExists = null;
    }
    SessionService.endSession(message || '');
    getAppStore().dispatch(refreshSessionInfo(null));
  }
}

const sessionManagementServiceInstance = new SessionManagementService();

export type {SessionManagementService};

export function getSessionManagementServiceInstance(): SessionManagementService {
  return sessionManagementServiceInstance;
}

const allowedModule = 'StatefulServicesTestHelper';
export function getSessionManagementServiceNewInstanceForTestOnly(callingModule: typeof allowedModule): SessionManagementService {
  if(callingModule !== allowedModule){
    throw new Error(`This function is only allowed to be called from ${allowedModule}`);
  }
  return new SessionManagementService();
}
